// UserProfile.js — profile page with follow/following counters + follow/unfollow (keeps MyMedia-style cache + lazy loading)

import React, { useEffect, useRef, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import Masonry from "react-masonry-css";

const CACHE_TTL_MS = 3 * 60 * 1000;

// 🔒 module-scope cache (same logic as MyMedia)
const USER_MEDIA_CACHE = {};

function base64UrlDecode(str) {
  try {
    const pad = "=".repeat((4 - (str.length % 4)) % 4);
    const base64 = (str + pad).replace(/-/g, "+").replace(/_/g, "/");
    const decoded = atob(base64);
    // handle unicode
    return decodeURIComponent(
      decoded
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
  } catch {
    return null;
  }
}

function decodeJwt(token) {
  if (!token || typeof token !== "string") return null;
  const parts = token.split(".");
  if (parts.length < 2) return null;
  const payload = base64UrlDecode(parts[1]);
  if (!payload) return null;
  try {
    return JSON.parse(payload);
  } catch {
    return null;
  }
}

function normalizeUserKey(v) {
  return String(v || "")
    .trim()
    .toLowerCase()
    .replace(/^@/, "");
}

function getAuthToken() {
  return (
    localStorage.getItem("kc_token") ||
    localStorage.getItem("token") ||
    localStorage.getItem("access_token") ||
    ""
  );
}

function getViewerIdentity() {
  // Try known localStorage keys first (fast path)
  const keys = ["auth_user", "user", "kc_user", "kc_profile", "profile"];
  for (const k of keys) {
    const raw = localStorage.getItem(k);
    if (!raw) continue;
    try {
      const obj = JSON.parse(raw);
      const username =
        obj?.username ||
        obj?.preferred_username ||
        obj?.handle ||
        obj?.email ||
        obj?.user?.username ||
        obj?.user?.email;
      if (username) return { username };
    } catch {
      // ignore
    }
  }

  // Fallback: decode JWT
  const token = getAuthToken();
  const jwt = decodeJwt(token);
  const username = jwt?.preferred_username || jwt?.email || jwt?.sub;
  return username ? { username } : null;
}

async function tryFetch(url, options = {}) {
  try {
    const res = await fetch(url, options);
    return res;
  } catch {
    return null;
  }
}

function LazyMediaVisual({ isImage, isVideo, src, title, sizeVariant = "m" }) {
  const holderRef = useRef(null);
  const [shouldLoad, setShouldLoad] = useState(false);

  useEffect(() => {
    const el = holderRef.current;
    if (!el) return;

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true);
          obs.disconnect();
        }
      },
      { rootMargin: "300px 0px" }
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div ref={holderRef} className={`media-thumb-wrap media-size-${sizeVariant}`}>
      {!shouldLoad ? (
        <div className="media-thumb media-thumb-skeleton" />
      ) : isImage ? (
        <img
          src={src}
          alt={title || "media"}
          className="media-thumb"
          loading="lazy"
          decoding="async"
        />
      ) : (
        <video src={src} preload="metadata" muted playsInline className="media-thumb" />
      )}
    </div>
  );
}

export default function UserProfilePage() {
  const { username } = useParams();

  const viewer = useMemo(() => getViewerIdentity(), []);
  const viewerKey = useMemo(() => normalizeUserKey(viewer?.username), [viewer]);

  const [profile, setProfile] = useState(null);
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);

  // follow states (best-effort: if backend doesn't expose follow status, user can still click Follow)
  const [followLoading, setFollowLoading] = useState(false);
  const [isFollowing, setIsFollowing] = useState(null); // null = unknown
  const [tip, setTip] = useState(null); // {type:'success'|'danger'|'info'|'warning', text:''}
  const tipTimerRef = useRef(null);

  const showTip = (text, type = "info") => {
    if (tipTimerRef.current) clearTimeout(tipTimerRef.current);
    setTip({ type, text });
    tipTimerRef.current = setTimeout(() => setTip(null), 2200);
  };

  const isFresh = (ts) => Date.now() - ts < CACHE_TTL_MS;

  const authHeaders = useMemo(() => {
    const token = getAuthToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const loadProfileAndMedia = async ({ bustCache = false } = {}) => {
    setLoading(true);

    if (!bustCache && USER_MEDIA_CACHE[username] && isFresh(USER_MEDIA_CACHE[username].ts)) {
      setProfile(USER_MEDIA_CACHE[username].profile);
      setMedia(USER_MEDIA_CACHE[username].media);
      setLoading(false);
      return;
    }

    try {
      const [pRes, mRes] = await Promise.all([
        fetch(`/users/${username}`),
        fetch(`/users/${username}/videos`),
      ]);

      const profileData = await pRes.json();
      const mediaData = await mRes.json();

      setProfile(profileData);
      setMedia(Array.isArray(mediaData) ? mediaData : []);

      USER_MEDIA_CACHE[username] = {
        profile: profileData,
        media: Array.isArray(mediaData) ? mediaData : [],
        ts: Date.now(),
      };
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const loadFollowStatus = async (profileData) => {
    // Only when logged-in AND not self profile
    if (!viewerKey) {
      setIsFollowing(null);
      return;
    }

    const targetKey = normalizeUserKey(profileData?.username || profileData?.handle || username);
    if (!targetKey || targetKey === viewerKey) {
      setIsFollowing(null);
      return;
    }

    // Try a few common endpoints (best-effort, non-breaking)
    const candidates = [
      `/users/${username}/follow-status`,
      `/users/${username}/is-followed`,
      `/follow/status?username=${encodeURIComponent(username)}`,
    ];

    for (const url of candidates) {
      const res = await tryFetch(url, { headers: { ...authHeaders } });
      if (!res) continue;
      if (res.ok) {
        try {
          const data = await res.json();
          const v =
            typeof data?.isFollowing === "boolean"
              ? data.isFollowing
              : typeof data?.is_following === "boolean"
              ? data.is_following
              : typeof data?.following === "boolean"
              ? data.following
              : null;
          if (v !== null) {
            setIsFollowing(v);
            return;
          }
        } catch {
          // ignore
        }
      }
    }

    // Unknown (backend might not expose status)
    setIsFollowing(null);
  };

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      await loadProfileAndMedia({ bustCache: false });
      if (cancelled) return;

      const p = USER_MEDIA_CACHE[username]?.profile || null;
      if (p) await loadFollowStatus(p);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [username]);

  const breakpointColumns = { default: 3, 1400: 2, 900: 1 };

  const pickSizeVariant = (idx, isVideo) => {
    if (isVideo) return "m";
    const mod = idx % 6;
    if (mod === 0) return "l";
    if (mod === 1 || mod === 2) return "m";
    return "s";
  };

  const handleFollowToggle = async () => {
    if (!profile) return;

    const targetKey = normalizeUserKey(profile?.username || profile?.handle || username);
    if (!viewerKey) {
      showTip("Connectez-vous pour suivre cet utilisateur.", "info");
      return;
    }
    if (!targetKey || targetKey === viewerKey) return;

    setFollowLoading(true);
    setTip(null);

    const wantsUnfollow = isFollowing === true;

    const followCandidates = wantsUnfollow
      ? [
          // Unfollow (try DELETE first)
          { method: "DELETE", url: `/users/${username}/follow`, body: null },
          { method: "POST", url: `/users/${username}/unfollow`, body: {} },
          { method: "POST", url: `/unfollow`, body: { username } },
        ]
      : [
          // Follow
          { method: "POST", url: `/users/${username}/follow`, body: {} },
          { method: "POST", url: `/follow`, body: { username } },
          { method: "POST", url: `/user-follow`, body: { username } },
        ];

    const headersBase = {
      "Content-Type": "application/json",
      ...authHeaders,
    };

    let ok = false;

    for (const c of followCandidates) {
      const opts = {
        method: c.method,
        headers: headersBase,
      };
      if (c.body && c.method !== "GET") opts.body = JSON.stringify(c.body);

      const res = await tryFetch(c.url, opts);
      if (!res) continue;

      if (res.ok) {
        ok = true;
        break;
      }
    }

    if (!ok) {
      showTip(
        "Impossible de mettre à jour l’abonnement (endpoint manquant ou non autorisé). Vérifiez la route backend follow/unfollow.",
        "danger"
      );
      setFollowLoading(false);
      return;
    }

    // ✅ No full reload: update local state + counters
    setIsFollowing(!wantsUnfollow);
    setProfile((prev) => {
      if (!prev) return prev;
      const cur = Number(prev.followers || 0);
      const next = wantsUnfollow ? Math.max(0, cur - 1) : cur + 1;
      return { ...prev, followers: next };
    });
    // Best-effort cache update (avoid stale count)
    try {
      if (USER_MEDIA_CACHE[username]?.profile) {
        const cur = Number(USER_MEDIA_CACHE[username].profile.followers || 0);
        const next = wantsUnfollow ? Math.max(0, cur - 1) : cur + 1;
        USER_MEDIA_CACHE[username].profile = {
          ...USER_MEDIA_CACHE[username].profile,
          followers: next,
        };
        USER_MEDIA_CACHE[username].ts = Date.now();
      }
    } catch {
      // ignore
    }

    showTip(wantsUnfollow ? "Abonnement retiré." : "Vous suivez maintenant cet utilisateur.", "success");

    setFollowLoading(false);
  };

  const isSelf = useMemo(() => {
    const targetKey = normalizeUserKey(profile?.username || profile?.handle || username);
    return !!viewerKey && !!targetKey && viewerKey === targetKey;
  }, [viewerKey, profile, username]);

  const styles = `
    .profile-card{
      border:none;border-radius:18px;overflow:hidden;
      background:#fff;box-shadow:0 8px 24px rgba(0,0,0,.08);
    }
    .profile-cover{
      height:220px;object-fit:cover;width:100%;
      background:#f1f5f9;
    }
    .profile-avatar{
      width:96px;height:96px;object-fit:cover;
      border-radius:999px;
      border:4px solid #fff;
      box-shadow:0 6px 18px rgba(0,0,0,.12);
      margin-top:-48px;
      background:#fff;
    }
    .profile-handle{
      color:#6b7280;
    }
    .profile-stats{
      display:flex;flex-wrap:wrap;gap:.75rem;
      align-items:center;justify-content:center;
      margin-top:.75rem;
    }
    .stat-pill{
      display:inline-flex;align-items:center;gap:.5rem;
      padding:.45rem .85rem;
      border-radius:999px;
      background:#f8fafc;
      border:1px solid rgba(0,0,0,.06);
      color:#334155;
      font-size:.92rem;
    }
    .stat-pill strong{font-weight:700}
    .follow-btn{
      min-width:140px;
    }
    .wv-msgLine{margin-top:.45rem;}
    .wv-msgLink{font-weight:600;text-decoration:underline;}
    .wv-msgLinkDisabled{opacity:.45;cursor:not-allowed;}
    .wv-miniTip{
      position:fixed;
      top:14px;
      right:14px;
      left:auto;
      transform:none;
      margin-top:0;
      padding:8px 10px;
      border-radius:12px;
      font-size:.92rem;
      line-height:1.15;
      box-shadow:0 12px 30px rgba(0,0,0,.18);
      z-index:50;
      max-width:min(420px, 92vw);
      white-space:normal;
      pointer-events:none;
    }
    .wv-miniTip-success{background:#16a34a;color:#fff;}
    .wv-miniTip-danger{background:#dc2626;color:#fff;}
    .wv-miniTip-info{background:#0f172a;color:#fff;}
    .wv-miniTip-warning{background:#f59e0b;color:#111827;}

    .media-card{
      border:none;border-radius:16px;overflow:hidden;
      background:#fff;box-shadow:0 4px 10px rgba(0,0,0,.08);
      transition:transform .15s ease,box-shadow .2s ease;
    }
    .media-card:hover{
      transform:translateY(-1px);
      box-shadow:0 8px 24px rgba(0,0,0,.12);
    }
    .media-thumb-wrap{
      position:relative;
      border-radius:inherit;
      overflow:hidden;
      background:#f3f4f6;
    }
    .media-thumb{
      width:100%;
      height:100%;
      object-fit:cover;
      display:block;
    }
    .media-thumb-skeleton{
      width:100%;height:100%;
      background:linear-gradient(90deg,#f2f2f2,#fafafa,#f2f2f2);
      background-size:200% 100%;
      animation:shimmer 1.2s infinite linear;
    }
    @keyframes shimmer{
      0%{background-position:200% 0}
      100%{background-position:-200% 0}
    }
    .media-size-s{height:220px}
    .media-size-m{height:280px}
    .media-size-l{height:340px}
    @media(max-width:992px){
      .media-size-s{height:210px}
      .media-size-m{height:250px}
      .media-size-l{height:300px}
    }
    .masonry-grid{display:flex;margin-left:-1rem;width:auto}
    .masonry-grid_column{padding-left:1rem}
    .masonry-grid_column>.card{margin-bottom:1rem}
  `;

  if (loading) {
    return (
      <div className="d-flex flex-column align-items-center py-5 text-muted">
        <div className="spinner-border mb-2" />
        <small>Chargement du profil…</small>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="container py-5 text-center text-danger">
        Utilisateur introuvable
      </div>
    );
  }

  const followers = Number(profile.followers || 0);
  const following = Number(profile.following || 0);
  const monthlyViews = Number(profile.monthly_views || 0);

  const showFollowBtn = !!viewerKey && !isSelf;

  return (
    <div className="container-fluid px-3 px-md-4 py-4">
      <style>{styles}</style>

      {/* PROFIL HEADER */}
      <div className="profile-card mb-4">
        {profile.cover_url ? (
          <img src={profile.cover_url} alt="" className="profile-cover" />
        ) : (
          <div className="profile-cover" />
        )}

        <div className="p-3 p-md-4">
          <div className="d-flex flex-column align-items-center text-center">
            {profile.avatar_url ? (
              <img src={profile.avatar_url} alt="" className="profile-avatar" />
            ) : (
              <div
                className="profile-avatar d-flex align-items-center justify-content-center text-white"
                style={{ background: "#94a3b8", fontWeight: 700, fontSize: 28 }}
              >
                {(profile.display_name || profile.username || username || "?")
                  .trim()
                  .charAt(0)
                  .toUpperCase()}
              </div>
            )}

            <div className="mt-2">
              <h5 className="mb-0">{profile.display_name || profile.username || username}</h5>
              <div className="profile-handle">
                {profile.handle
                  ? profile.handle
                  : profile.username
                  ? `@${profile.username}`
                  : `@${username}`}
              </div>
              {profile.bio ? <p className="mt-2 mb-0">{profile.bio}</p> : null}
            </div>

            <div className="profile-stats">
              <div className="stat-pill" title="Nombre d’abonnés">
                <i className="bi bi-people" />
                <strong>{followers}</strong>
                <span>abonnés</span>
              </div>

              <div className="stat-pill" title="Nombre d’abonnements">
                <i className="bi bi-person-plus" />
                <strong>{following}</strong>
                <span>abonnements</span>
              </div>

              <div className="stat-pill" title="Vues mensuelles (si disponible)">
                <i className="bi bi-graph-up" />
                <strong>{monthlyViews}</strong>
                <span>vues/mois</span>
              </div>
            </div>
            {showFollowBtn ? (
              <div className="d-inline-flex align-items-center gap-2">
                <button
                type="button"
                className={`btn ${
                  isFollowing ? "btn-outline-secondary" : "btn-primary"
                } rounded-pill mt-3 follow-btn`}
                disabled={followLoading}
                onClick={handleFollowToggle}
              >
                {followLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Mise à jour…
                  </>
                ) : isFollowing ? (
                  <>
                    <i className="bi bi-check2-circle me-2" />
                    Abonné
                  </>
                ) : (
                  <>
                    <i className="bi bi-person-plus me-2" />
                    Suivre
                  </>
                )}
              </button>
                {viewerKey && !isSelf ? (
                  isFollowing === true ? (
                    <Link
                      to={`/messages?to=${encodeURIComponent(username)}`}
                      className="btn btn-outline-secondary rounded-pill mt-3 follow-btn d-inline-flex align-items-center gap-2"
                      title="Envoyer un message"
                    >
                      <i className="bi bi-chat-dots" />
                      <span>Message</span>
                    </Link>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-outline-secondary rounded-pill mt-3 follow-btn d-inline-flex align-items-center gap-2"
                      disabled
                      title="Suivez d’abord pour pouvoir envoyer un message."
                    >
                      <i className="bi bi-chat-dots" />
                      <span>Message</span>
                    </button>
                  )
                ) : null}


                {tip?.text ? (
                  <div className={`wv-miniTip wv-miniTip-${tip.type || "info"}`}>
                    {tip.text}
                  </div>
                ) : null}
              </div>
            ) : null}

            {!viewerKey ? (
              <small className="text-muted mt-2">
                Connectez-vous pour suivre des utilisateurs.
              </small>
            ) : null}
          </div>
        </div>
      </div>

      {/* MEDIA GRID */}
      {media.length === 0 ? (
        <div className="text-muted text-center py-5">
          Aucun média public pour le moment.
        </div>
      ) : (
        <Masonry
          breakpointCols={breakpointColumns}
          className="masonry-grid"
          columnClassName="masonry-grid_column"
        >
          {media.map((m, idx) => {
            const isImage = /\.(jpg|jpeg|png|webp|gif)$/i.test(m.file_path);
            const isVideo = /\.(mp4|mov|webm|mkv)$/i.test(m.file_path);
            const sizeVariant = pickSizeVariant(idx, isVideo);

            return (
              <div key={m.id} className="card media-card">
                <Link
                  to={`/mediaDetails/${m.id}`}
                  className="text-decoration-none text-dark"
                >
                  <LazyMediaVisual
                    isImage={isImage}
                    isVideo={isVideo}
                    src={`/uploads/${m.file_path}`}
                    title={m.title}
                    sizeVariant={sizeVariant}
                  />
                  <div className="card-body">
                    <strong className="d-block text-truncate">
                      {m.title || "Sans titre"}
                    </strong>
                  </div>
                </Link>
              </div>
            );
          })}
        </Masonry>
      )}
    </div>
  );
}
